import React from 'react';
import clsx from 'clsx';

import Loading from './loading';

import styles from './PrimaryButton.module.scss';
import Link from 'next/link';

type ButtonVariant = 'text' | 'outlined' | 'contained';

interface PrimaryButtonProps {
  id?: string;
  className?: string;
  iconLeft?: JSX.Element | null;
  type?: 'button' | 'submit' | 'reset';
  size?: 'small' | 'medium' | 'large';
  variant?: ButtonVariant;
  settings?: any;
  error?: boolean;
  flat?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  invalid?: boolean;
  href?: string;
  fullWidth?: boolean;
  prefetch?: boolean;
}

const PrimaryButton = ({
  id,
  className,
  iconLeft,
  href,
  type = 'button',
  variant = 'contained',
  size = 'medium',
  settings,
  error,
  disabled,
  loading,
  onClick,
  children,
  invalid,
  fullWidth,
  prefetch = true,
}: React.PropsWithChildren<PrimaryButtonProps>) => {
  const baseColor = error
    ? '#F44336'
    : settings?.whistle_guest_primary_button_color;

  let style: any = {
    color: baseColor,
  };

  if (variant === 'outlined') {
    style = {
      border: `1px solid ${baseColor}`,
      backgroundColor: settings?.whistle_guest_secondary_button_color,
      color: baseColor,
    };
  } else if (variant === 'contained') {
    style = {
      backgroundColor: baseColor,
      color: settings?.whistle_guest_secondary_button_color,
    };
  }

  if (href && !disabled) {
    return (
      <div className={styles.ButtonWrapper}>
        <Link
          id={id}
          className={clsx(
            styles.Button,
            (invalid || loading) && styles.invalid,
            fullWidth && styles.fullWidth,
            iconLeft && styles.iconLeft,
            size === 'small' && styles.small,
            className,
          )}
          style={style}
          href={href}
          prefetch={prefetch}
        >
          {iconLeft && <span className={styles.icon}>{iconLeft}</span>}
          {children}
        </Link>
      </div>
    );
  }

  return (
    <div className={styles.ButtonWrapper}>
      <button
        id={id}
        type={type}
        className={clsx(
          styles.Button,
          (invalid || loading) && styles.invalid,
          iconLeft && styles.iconLeft,
          size === 'small' && styles.small,
          className,
        )}
        style={style}
        disabled={disabled}
        onClick={onClick}
      >
        {loading ? (
          <Loading />
        ) : (
          <>
            {iconLeft && <span className={styles.icon}>{iconLeft}</span>}
            {children}
          </>
        )}
      </button>
    </div>
  );
};

export default PrimaryButton;
